import { createTheme, ThemeOptions } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

// ═══════════════════════════════════════════════════════════════
//  🌌 Dark Futuristic Theme — Cainguás Cooperativa
//  Glassmorphism + neon accents + cosmic background
// ═══════════════════════════════════════════════════════════════

const brand = {
  neonBlue: '#00B4FF',
  neonCyan: '#00E5FF',
  deepNavy: '#0A1628',
  darkNavy: '#0D1F3C',
  midNavy: '#132D50',
  surfaceGlass: 'rgba(15, 30, 55, 0.65)',
  surfaceSolid: '#0F1E37',
  borderGlow: 'rgba(0, 180, 255, 0.15)',
  borderSubtle: 'rgba(255, 255, 255, 0.08)',
  textPrimary: '#E8F0FA',
  textSecondary: '#8BA3C0',
  textMuted: '#5A7394',
};

const colors = {
  primary: {
    main: brand.neonBlue,
    light: brand.neonCyan,
    dark: '#0088CC',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#7C4DFF',
    light: '#B388FF',
    dark: '#651FFF',
    contrastText: '#ffffff',
  },
  success: {
    main: '#00E676',
    light: '#69F0AE',
    dark: '#00C853',
    contrastText: '#0A1628',
  },
  warning: {
    main: '#FFB300',
    light: '#FFD54F',
    dark: '#FF8F00',
    contrastText: '#0A1628',
  },
  error: {
    main: '#FF5252',
    light: '#FF8A80',
    dark: '#FF1744',
    contrastText: '#ffffff',
  },
  info: {
    main: brand.neonBlue,
    light: brand.neonCyan,
    dark: '#0088CC',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#E8F0FA',
    100: '#C5D4E8',
    200: '#8BA3C0',
    300: '#5A7394',
    400: '#3D5570',
    500: '#2A3F5A',
    600: '#1D3050',
    700: '#132D50',
    800: '#0D1F3C',
    900: '#0A1628',
  },
};

// ═══════════════════════════════════════════════════════════════
const glass = {
  card: `${brand.surfaceGlass}`,
  border: `1px solid ${brand.borderSubtle}`,
  blur: 'blur(16px) saturate(180%)',
  glow: `0 0 20px rgba(0, 180, 255, 0.08)`,
  glowHover: `0 0 30px rgba(0, 180, 255, 0.15), 0 8px 32px rgba(0,0,0,0.3)`,
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    grey: colors.grey,
    background: {
      default: brand.deepNavy,
      paper: brand.surfaceSolid,
    },
    text: {
      primary: brand.textPrimary,
      secondary: brand.textSecondary,
      disabled: brand.textMuted,
    },
    divider: brand.borderSubtle,
  },

  // ── Tipografía ──────────────────────────────────────────────
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em', color: brand.textPrimary },
    h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em', color: brand.textPrimary },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4, color: brand.textPrimary },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, color: brand.textPrimary },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5, color: brand.textPrimary },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, color: brand.textPrimary },
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.6 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.57 },
    body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.75, textTransform: 'none' as const },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66, color: brand.textSecondary },
    overline: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 2.5, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: brand.textMuted },
  },

  shape: { borderRadius: 12 },
  spacing: 8,
  breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 } },

  // ── Sombras (azul neón sutil) ────────────────────────────────
  shadows: [
    'none',
    `0 1px 3px rgba(0,0,0,0.4), ${glass.glow}`,
    `0 2px 6px rgba(0,0,0,0.4), ${glass.glow}`,
    `0 4px 12px rgba(0,0,0,0.4), ${glass.glow}`,
    '0 6px 16px rgba(0,0,0,0.5)',
    '0 8px 24px rgba(0,0,0,0.5)',
    '0 10px 28px rgba(0,0,0,0.5)',
    '0 12px 32px rgba(0,0,0,0.5)',
    '0 14px 36px rgba(0,0,0,0.5)',
    '0 16px 40px rgba(0,0,0,0.5)',
    '0 18px 44px rgba(0,0,0,0.5)',
    '0 20px 48px rgba(0,0,0,0.5)',
    '0 22px 52px rgba(0,0,0,0.5)',
    '0 24px 56px rgba(0,0,0,0.5)',
    '0 26px 60px rgba(0,0,0,0.5)',
    '0 28px 64px rgba(0,0,0,0.5)',
    '0 30px 68px rgba(0,0,0,0.5)',
    '0 32px 72px rgba(0,0,0,0.5)',
    '0 34px 76px rgba(0,0,0,0.5)',
    '0 36px 80px rgba(0,0,0,0.5)',
    '0 38px 84px rgba(0,0,0,0.5)',
    '0 40px 88px rgba(0,0,0,0.5)',
    '0 42px 92px rgba(0,0,0,0.5)',
    '0 44px 96px rgba(0,0,0,0.5)',
    '0 46px 100px rgba(0,0,0,0.5)',
  ],

  // ── Componentes ─────────────────────────────────────────────
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.deepNavy,
          scrollbarColor: `${colors.grey[500]} transparent`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: colors.grey[500],
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: brand.neonBlue,
          },
        },
      },
    },

    // AppBar — fondo glass oscuro
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, rgba(13,31,60,0.85) 0%, rgba(10,22,40,0.95) 100%)`,
          backdropFilter: glass.blur,
          borderBottom: `1px solid ${brand.borderGlow}`,
          boxShadow: `0 4px 30px rgba(0,0,0,0.3), inset 0 -1px 0 ${brand.borderGlow}`,
        },
      },
    },

    // Drawer — sidebar oscuro con borde glow
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(180deg, ${brand.darkNavy} 0%, ${brand.deepNavy} 100%)`,
          borderRight: `1px solid ${brand.borderGlow}`,
          boxShadow: `4px 0 30px rgba(0,0,0,0.3)`,
        },
      },
    },

    // Cards — GLASSMORPHISM
    MuiCard: {
      styleOverrides: {
        root: {
          background: glass.card,
          backdropFilter: glass.blur,
          border: glass.border,
          borderRadius: 16,
          boxShadow: glass.glow,
          transition: 'all 0.3s ease',
          '&:hover': {
            border: `1px solid ${brand.borderGlow}`,
            boxShadow: glass.glowHover,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': { paddingBottom: 16 },
        },
      },
    },

    // Botones — neón glow
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
          padding: '8px 20px',
          transition: 'all 0.25s ease',
          letterSpacing: '0.01em',
        },
        contained: {
          background: `linear-gradient(135deg, ${brand.neonBlue} 0%, ${colors.primary.dark} 100%)`,
          boxShadow: `0 2px 12px rgba(0,180,255,0.3)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${brand.neonCyan} 0%, ${brand.neonBlue} 100%)`,
            boxShadow: `0 4px 20px rgba(0,180,255,0.5)`,
            transform: 'translateY(-1px)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        outlined: {
          borderColor: brand.borderGlow,
          color: brand.neonBlue,
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            borderColor: brand.neonBlue,
            backgroundColor: 'rgba(0,180,255,0.08)',
            boxShadow: `0 0 15px rgba(0,180,255,0.15)`,
          },
        },
        text: {
          color: brand.textSecondary,
          '&:hover': {
            backgroundColor: 'rgba(0,180,255,0.08)',
            color: brand.neonBlue,
          },
        },
      },
    },

    // Chips — con brillo
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderColor: brand.borderSubtle,
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            background: `linear-gradient(135deg, ${brand.neonBlue} 0%, ${colors.primary.dark} 100%)`,
            boxShadow: '0 2px 8px rgba(0,180,255,0.3)',
          },
          '&.MuiChip-colorSuccess': {
            background: 'linear-gradient(135deg, #00E676 0%, #00C853 100%)',
            boxShadow: '0 2px 8px rgba(0,230,118,0.3)',
            color: '#0A1628',
          },
          '&.MuiChip-colorError': {
            background: 'linear-gradient(135deg, #FF5252 0%, #FF1744 100%)',
            boxShadow: '0 2px 8px rgba(255,82,82,0.3)',
          },
          '&.MuiChip-colorWarning': {
            background: 'linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)',
            boxShadow: '0 2px 8px rgba(255,179,0,0.3)',
            color: '#0A1628',
          },
        },
        outlined: {
          borderColor: brand.borderSubtle,
          '&.MuiChip-colorSuccess': { borderColor: 'rgba(0,230,118,0.4)', color: '#69F0AE' },
          '&.MuiChip-colorError': { borderColor: 'rgba(255,82,82,0.4)', color: '#FF8A80' },
          '&.MuiChip-colorWarning': { borderColor: 'rgba(255,179,0,0.4)', color: '#FFD54F' },
          '&.MuiChip-colorPrimary': { borderColor: 'rgba(0,180,255,0.4)', color: brand.neonBlue },
        },
      },
    },

    // Paper — glass
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 14,
        },
      },
    },

    // TextField — bordes glass
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: 'rgba(10,22,40,0.5)',
            transition: 'all 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.borderSubtle,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,180,255,0.3)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: brand.neonBlue,
              boxShadow: `0 0 12px rgba(0,180,255,0.15)`,
            },
          },
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'rgba(10,22,40,0.5)',
        },
      },
    },

    // Tabla — filas glass
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'rgba(0,180,255,0.06)',
          fontWeight: 600,
          color: brand.textSecondary,
          fontSize: '0.8125rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.04em',
          borderBottom: `1px solid ${brand.borderGlow}`,
        },
        body: {
          fontSize: '0.875rem',
          borderBottom: `1px solid ${brand.borderSubtle}`,
          color: brand.textPrimary,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0,180,255,0.05) !important',
          },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },

    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 7,
          backgroundColor: 'rgba(255,255,255,0.06)',
        },
        bar: {
          borderRadius: 6,
          background: `linear-gradient(90deg, ${brand.neonBlue}, ${brand.neonCyan})`,
        },
      },
    },

    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: '0.9375rem',
          minHeight: 48,
          color: brand.textSecondary,
          '&.Mui-selected': {
            fontWeight: 600,
            color: brand.neonBlue,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: `linear-gradient(90deg, ${brand.neonBlue}, ${brand.neonCyan})`,
          boxShadow: '0 0 10px rgba(0,180,255,0.5)',
        },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.9rem',
        },
        colorDefault: {
          backgroundColor: brand.midNavy,
          color: brand.neonBlue,
          border: `1px solid ${brand.borderGlow}`,
        },
      },
    },

    // Dialog — glass
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: brand.darkNavy,
          borderRadius: 18,
          border: `1px solid ${brand.borderGlow}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.5), 0 0 40px rgba(0,180,255,0.1)',
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.8rem',
          fontWeight: 500,
          backgroundColor: brand.midNavy,
          border: `1px solid ${brand.borderGlow}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        },
      },
    },

    // Alert — glass variant
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
          backdropFilter: 'blur(8px)',
        },
        standardSuccess: { backgroundColor: 'rgba(0,230,118,0.12)', color: '#69F0AE', border: '1px solid rgba(0,230,118,0.2)' },
        standardError: { backgroundColor: 'rgba(255,82,82,0.12)', color: '#FF8A80', border: '1px solid rgba(255,82,82,0.2)' },
        standardWarning: { backgroundColor: 'rgba(255,179,0,0.12)', color: '#FFD54F', border: '1px solid rgba(255,179,0,0.2)' },
        standardInfo: { backgroundColor: 'rgba(0,180,255,0.12)', color: brand.neonBlue, border: `1px solid ${brand.borderGlow}` },
        filledSuccess: { background: 'linear-gradient(135deg, #00C853 0%, #00962E 100%)' },
        filledError: { background: 'linear-gradient(135deg, #FF5252 0%, #D32F2F 100%)' },
        filledWarning: { background: 'linear-gradient(135deg, #FFB300 0%, #E65100 100%)' },
      },
    },

    // ListItemButton — sidebar items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 10px',
          padding: '10px 16px',
          transition: 'all 0.25s ease',
          color: brand.textSecondary,
          '&.Mui-selected': {
            backgroundColor: 'rgba(0,180,255,0.12)',
            color: brand.neonBlue,
            borderLeft: `3px solid ${brand.neonBlue}`,
            boxShadow: `inset 0 0 20px rgba(0,180,255,0.06), 0 0 15px rgba(0,180,255,0.08)`,
            '&:hover': {
              backgroundColor: 'rgba(0,180,255,0.15)',
            },
            '& .MuiListItemIcon-root': {
              color: brand.neonBlue,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.04)',
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: brand.textMuted,
        },
      },
    },

    // Badge
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.7rem',
          boxShadow: '0 0 8px rgba(0,180,255,0.3)',
        },
      },
    },

    // Menu
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: brand.darkNavy,
          border: `1px solid ${brand.borderGlow}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(0,180,255,0.05)',
          backdropFilter: glass.blur,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0,180,255,0.08)',
          },
        },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: brand.borderSubtle,
        },
      },
    },

    // TablePagination
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: brand.textSecondary,
          borderTop: `1px solid ${brand.borderSubtle}`,
        },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.06)',
        },
      },
    },

    // CircularProgress
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: brand.neonBlue },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: brand.neonBlue,
            '& + .MuiSwitch-track': {
              backgroundColor: brand.neonBlue,
              opacity: 0.5,
            },
          },
        },
        track: {
          backgroundColor: colors.grey[500],
        },
      },
    },

    // InputLabel
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: brand.textSecondary,
          '&.Mui-focused': {
            color: brand.neonBlue,
          },
        },
      },
    },
  },

  // ── Transiciones ────────────────────────────────────────────
  transitions: {
    duration: {
      shortest: 150, shorter: 200, short: 250, standard: 300,
      complex: 375, enteringScreen: 200, leavingScreen: 180,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
};

export const theme = createTheme(themeOptions, esES);
export { colors, brand, glass };
export const darkTheme = theme; // Ya es dark
export default theme;